<template>
  <v-row>
    <v-col cols="12">
      <v-card class="usr-card" flat>
        <!-- ================= HEADER BAND ================= -->
        <div class="usr-header pl-base-bg">
          <div class="usr-header-left">
            <v-avatar size="48" class="usr-avatar">
              <v-img v-if="user?.profilePicture" :src="user.profilePicture" />
              <span v-else class="white--text text-h5">
                {{ user?.name?.charAt(0)?.toUpperCase() }}
              </span>
            </v-avatar>
            <div>
              <div class="usr-title">{{ user?.name }}</div>
              <div class="usr-subtitle">{{ user?.email }}</div>
            </div>
          </div>

          <div class="usr-header-right">
            <v-chip
              :color="user?.userType === 'admin' ? 'pink' : 'blue'"
              dark
              small
              class="text-uppercase font-weight-bold usr-role-chip"
            >
              {{ user?.userType }}
            </v-chip>

            <v-chip
              :color="user?.isActive ? 'green' : 'grey'"
              dark
              small
              class="text-uppercase font-weight-bold usr-status-chip"
            >
              {{ user?.isActive ? "Active" : "Inactive" }}
            </v-chip>

            <v-btn
              small
              class="usr-edit-btn"
              @click="$router.push(`/users/${user._id}/edit`)"
            >
              <v-icon small left>mdi-pencil</v-icon>
              Edit
            </v-btn>
          </div>
        </div>

        <div class="pa-6">
          <!-- ================= ACCOUNT DETAILS ================= -->
          <div class="usr-section-label">Account Details</div>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-account-outline</v-icon>
                  Full Name
                </div>
                <div class="usr-info-value">{{ user?.name || "-" }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-email-outline</v-icon>
                  Email
                </div>
                <div class="usr-info-value">{{ user?.email || "-" }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-phone-outline</v-icon>
                  Phone
                </div>
                <div class="usr-info-value">{{ user?.phone || "-" }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-at</v-icon>
                  Username
                </div>
                <div class="usr-info-value">{{ user?.username || "-" }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-shield-account-outline</v-icon>
                  User Type
                </div>
                <div class="usr-info-value text-capitalize">
                  {{ user?.userType || "-" }}
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- ================= ACCESS & STATUS ================= -->
          <div class="usr-section-label mt-6">Access &amp; Status</div>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-toggle-switch-outline</v-icon>
                  Account Status
                </div>
                <div
                  class="usr-info-value"
                  :class="user?.isActive ? 'green--text' : 'grey--text'"
                >
                  {{ user?.isActive ? "Active" : "Inactive" }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-email-check-outline</v-icon>
                  Email Verified
                </div>
                <div
                  class="usr-info-value"
                  :class="user?.isEmailVerified ? 'green--text' : 'red--text'"
                >
                  {{ user?.isEmailVerified ? "Verified" : "Not Verified" }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div
                class="usr-info-card"
                :class="{ 'usr-info-card--danger': isExpired, 'usr-info-card--warn': isExpiringSoon }"
              >
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-calendar-clock-outline</v-icon>
                  Payment Expiry
                </div>
                <div
                  class="usr-info-value"
                  :class="{ 'red--text': isExpired, 'orange--text text--darken-2': isExpiringSoon }"
                >
                  {{ formatDate(user?.paymentExpiryDate) }}
                  <span v-if="isExpired" class="usr-badge usr-badge--danger">Expired</span>
                  <span v-else-if="isExpiringSoon" class="usr-badge usr-badge--warn">Expiring soon</span>
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-calendar-plus-outline</v-icon>
                  Created On
                </div>
                <div class="usr-info-value">{{ formatDate(user?.createdAt) }}</div>
              </div>
            </v-col>
          </v-row>

          <!-- ================= TEAM ACCESS (multi-user) ================= -->
          <div class="usr-section-label mt-6">Team Access</div>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-account-group-outline</v-icon>
                  Role
                </div>
                <div class="usr-info-value">{{ teamRoleLabel }}</div>
              </div>
            </v-col>

            <v-col v-if="user?.ownerUserId" cols="12" sm="6" md="5">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-domain</v-icon>
                  Business Owner
                </div>
                <div class="usr-info-value">
                  {{ user.ownerUserId.name || "-" }} ({{ user.ownerUserId.email || "-" }})
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Only present when this user is an Owner — see
               Terms & Conditions Section 4: Team Member access is
               licensed for the Owner's own business only. A large or
               unfamiliar-looking roster here is worth a closer look. -->
          <template v-if="user?.teamMembers?.length">
            <div class="usr-team-roster mt-4">
              <div class="usr-team-roster-head">
                <span>Team Members ({{ user.teamMembers.length }})</span>
              </div>
              <div
                v-for="member in user.teamMembers"
                :key="member._id"
                class="usr-team-roster-row"
              >
                <div>
                  <div class="usr-team-roster-name">{{ member.name }}</div>
                  <div class="usr-team-roster-email">{{ member.email }}</div>
                </div>
                <div class="usr-team-roster-meta">
                  <v-chip x-small :color="member.isActive ? 'green' : 'grey'" dark>
                    {{ member.isActive ? "Active" : "Deactivated" }}
                  </v-chip>
                  <v-chip x-small outlined class="ml-2">
                    {{ member.teamRole === "VIEW_ONLY" ? "View Only" : "Full Access" }}
                  </v-chip>
                </div>
              </div>
            </div>
          </template>

          <!-- ================= BUSINESS PROFILE ================= -->
          <div class="usr-section-label mt-6">Business Profile</div>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-domain</v-icon>
                  Business Name
                </div>
                <div class="usr-info-value">{{ business?.businessName || "-" }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-file-document-outline</v-icon>
                  GSTIN
                </div>
                <div class="usr-info-value">{{ business?.gstin || "-" }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-email-outline</v-icon>
                  Business Email
                </div>
                <div class="usr-info-value">{{ business?.email || "-" }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-phone-outline</v-icon>
                  Business Phone
                </div>
                <div class="usr-info-value">{{ business?.phone || "-" }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="6">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-map-marker-outline</v-icon>
                  Address
                </div>
                <div class="usr-info-value">{{ business?.address || "-" }}</div>
              </div>
            </v-col>
          </v-row>

          <!-- ================= BANK ACCOUNT ================= -->
          <div class="usr-section-label mt-6">Bank Account</div>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-bank-outline</v-icon>
                  Bank Name
                </div>
                <div class="usr-info-value">{{ bankAccount?.bankName || "-" }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-numeric</v-icon>
                  Account Number
                </div>
                <div class="usr-info-value">{{ bankAccount?.accountNumber || "-" }}</div>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-identifier</v-icon>
                  IFSC
                </div>
                <div class="usr-info-value">{{ bankAccount?.ifsc || "-" }}</div>
              </div>
            </v-col>
          </v-row>

          <!-- ================= TERMS & CONDITIONS ================= -->
          <div class="usr-section-label mt-6">Terms &amp; Conditions</div>
          <v-row>
            <v-col cols="12">
              <div class="usr-info-card">
                <div class="usr-info-label">
                  <v-icon size="14" class="mr-1">mdi-file-document-edit-outline</v-icon>
                  Content
                </div>
                <div class="usr-info-value usr-info-value--terms">{{ terms?.content || "-" }}</div>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import api from "@/services/api";

export default {
  name: "ViewUser",

  data() {
    return {
      user: null,
      business: null,
      bankAccount: null,
      terms: null,
    };
  },

  async created() {
    const userId = this.$route.params.id;
    this.user = await this.$store.dispatch("users/getUserById", userId);

    const [businessRes, bankRes, termsRes] = await Promise.allSettled([
      api.get("/business/profile", { params: { userId } }),
      api.get("/bank/bank-account", { params: { userId } }),
      api.get("/terms", { params: { userId } }),
    ]);

    if (businessRes.status === "fulfilled") this.business = businessRes.value.data;
    if (bankRes.status === "fulfilled") this.bankAccount = bankRes.value.data;
    if (termsRes.status === "fulfilled") this.terms = termsRes.value.data;
  },

  computed: {
    isExpired() {
      if (!this.user?.paymentExpiryDate) return false;
      const expiry = new Date(this.user.paymentExpiryDate);
      if (isNaN(expiry.getTime())) return false;
      return expiry.getTime() < Date.now();
    },

    isExpiringSoon() {
      if (!this.user?.paymentExpiryDate || this.isExpired) return false;
      const expiry = new Date(this.user.paymentExpiryDate);
      if (isNaN(expiry.getTime())) return false;
      const diffDays = (expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      return diffDays >= 0 && diffDays <= 5;
    },

    // See server/src/utils/tenant.js — ownerUserId is null for an
    // owner/solo account, set for a team member (multi-user access).
    teamRoleLabel() {
      if (!this.user?.ownerUserId) return "Owner";
      return this.user.teamRole === "VIEW_ONLY" ? "View Only" : "Full Access";
    },
  },

  methods: {
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.usr-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

/* ================= HEADER BAND ================= */
.usr-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
}

.usr-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.usr-avatar {
  border: 2px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.16);
}

.usr-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.usr-subtitle {
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.usr-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.usr-role-chip,
.usr-status-chip {
  letter-spacing: 0.04em;
}

.usr-edit-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
.usr-edit-btn:hover {
  background: rgba(255, 255, 255, 0.26) !important;
}

/* ================= SECTIONS ================= */
.usr-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 12px;
}

.usr-info-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 14px 16px;
  height: 100%;
}

.usr-info-card--danger {
  background: #fdecea;
  border-color: #f6d3cf;
}

.usr-info-card--warn {
  background: #fff7e6;
  border-color: #ffe6b0;
}

.usr-team-roster {
  border: 1px solid #eef1f5;
  border-radius: 0;
  overflow: hidden;
}

.usr-team-roster-head {
  background: #f8fafc;
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  border-bottom: 1px solid #eef1f5;
}

.usr-team-roster-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
}
.usr-team-roster-row:last-child {
  border-bottom: none;
}

.usr-team-roster-name {
  font-weight: 700;
  font-size: 13.5px;
  color: #0f172a;
}

.usr-team-roster-email {
  font-size: 12px;
  color: #64748b;
}

.usr-team-roster-meta {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.usr-info-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.usr-info-value {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.usr-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 20px;
}

.usr-badge--danger {
  background: #f8d7d3;
  color: #c62828;
}

.usr-badge--warn {
  background: #ffe9bd;
  color: #b8720a;
}

.usr-info-value--terms {
  white-space: pre-wrap;
  font-weight: 400;
  line-height: 1.5;
}

@media (max-width: 600px) {
  .usr-header {
    padding: 12px 14px;
  }

  .pa-6 {
    padding: 16px !important;
  }
}
</style>
